import React from "react";

import { NavLink, useLocation } from "react-router-dom";
import { useState, useEffect, useContext } from "react";
import { Box, HStack, Text, useColorModeValue } from "@chakra-ui/react";
import { AuthContext } from "context/index.js";
import { canAccessRoute } from "utils/modulePermissions";

export function SidebarLinks(props) {
  const location = useLocation();

  const activeColor = useColorModeValue("gray.700", "white");
  const textColor = useColorModeValue("secondaryGray.500", "white");
  const activeIcon = useColorModeValue("brand.500", "white");
  const hoverBg = useColorModeValue("gray.100", "whiteAlpha.200");

  const authContext = useContext(AuthContext);
  // El menu se dibuja con los permisos efectivos de la sesion: ya traen el
  // estado global de cada modulo, asi que no hace falta consultar el catalogo.
  const user = authContext?.user;

  // La barra se pinta en dos sitios: la lista principal y el pie anclado encima de la
  // versión (`pinned`). Ambos reciben todas las rutas para saber cuál está activa; si
  // no, la lista dejaba resaltada la última página suya mientras estabas en una anclada.
  const { routes, isCollapsed, onNavigate, pinned = false } = props;

  const [lastActivePath, setLastActivePath] = useState("");

  const activeRoute = (routePath) => {
    if (!routePath) return false;
    return location.pathname.toLowerCase().startsWith(routePath.toLowerCase());
  };

  const handleRouteClick = (route) => {
    localStorage.setItem("PaginaActual", route.name);
    if (typeof onNavigate === "function") {
      onNavigate();
    }
  };

  const flattenRoutes = (routesList) => {
    return routesList.flatMap((route) => {
      if (route.category) return flattenRoutes(route.items || []);
      return route.layout ? [route] : [];
    });
  };

  useEffect(() => {
    const flatRoutes = flattenRoutes(routes || []);
    const current = flatRoutes.find((route) => {
      const fullPath = `${route.layout}${route.path}`;
      return route.show && activeRoute(fullPath);
    });

    if (current) {
      setLastActivePath(`${current.layout}${current.path}`);
    }
  // El efecto solo debe volver a ejecutarse con los valores listados: 'activeRoute' and 'flattenRoutes'
  // se recrea en cada render y agregarla dispararia la carga en bucle.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, routes]);

  const createLinks = (routes) => {
    return routes.map((route, index) => {
      if (route.category) {
        return (
          <React.Fragment key={index}>
            {!isCollapsed && (
              <Text
                fontSize="md"
                color={activeColor}
                fontWeight="bold"
                mx="auto"
                ps={{ sm: "10px", xl: "16px" }}
                pt="18px"
                pb="12px"
              >
                {route.name}
              </Text>
            )}
            {createLinks(route.items)}
          </React.Fragment>
        );
      } else if (route.layout === "/admin" || route.layout === "/auth") {
        const fullPath = `${route.layout}${route.path}`;
        const isActive = route.show && activeRoute(fullPath);
        const isLastActive = route.show && lastActivePath === fullPath;
        if (!route.show || !canAccessRoute(user, route) || Boolean(route.pinned) !== pinned) {
          return null;
        }

        return (
          <NavLink
            key={index}
            to={fullPath}
            onClick={() => handleRouteClick(route)}
          >
            <Box>
              <HStack
                spacing="12px"
                py="10px"
                ps={isCollapsed ? "12px" : "20px"}
                pe="8px"
                align="center"
                _hover={{ bg: hoverBg }}
                bg={isActive || isLastActive ? hoverBg : "transparent"}
                borderRadius="12px"
              >
                <Box
                  color={isActive || isLastActive ? activeIcon : textColor}
                >
                  {route.icon}
                </Box>

                {!isCollapsed && (
                  <Text
                    color={isActive || isLastActive ? activeColor : textColor}
                    fontWeight={isActive || isLastActive ? "bold" : "normal"}
                    noOfLines={2}
                  >
                    {route.name}
                  </Text>
                )}
              </HStack>
            </Box>
          </NavLink>
        );
      }

      // Las rutas que no son categoría ni enlace no pintan nada.
      return null;
    });
  };

  return createLinks(routes);
}

export default SidebarLinks;
